/* eslint-disable react-hooks/rules-of-hooks */
import React from "react";
import nookies from "nookies";
import jwt_decode from "jwt-decode";
import { VehicleDetailsProps } from "../../vehicleDetails/vehicle-details-props.type";
import VehicleDetailsIDP from "../../content/VehicleDetailsIDP";
import VehicleDetailsAuth0 from "../../content/VehicleDetailsAuth0";
import VehicleDetailsSkeleton from "../../components/VehicleDetailsSkeleton";
import { FeatureFlagManager } from "bca-feature-flag-provider";
import redirectToIdp from "../../sso/idp";
import getEnvironment from "../../helpers/getEnvironment";
import getToken from "../../sso/getToken";
import hasUserRole from "../../utilities/userRole.functions";
import UserRoles from "../../utilities/userRoles";
import { getVehicleDetails } from "../../vehicleDetails/getVehicleDetails";

const VehicleDetails: React.FunctionComponent<VehicleDetailsProps> = (
  props
) => {
  if (props.isToUseAuthZero && !props.vrm) {
    // Auth0 callback routed here — render skeleton while AuthProvider handles the code exchange
    return <VehicleDetailsSkeleton isToUseAuthZero={true} />;
  }
  if (props.isToUseAuthZero) {
    return (
      <VehicleDetailsAuth0
        isToUseAuthZero={props.isToUseAuthZero}
        authLogoutUrl={props.authLogoutUrl}
        impersonatingUser={props.impersonatingUser}
        vrm={props.vrm}
        appConfigurationUrl={props.appConfigurationUrl}
        fromSearch={props.FromSearch}
        resolvedUrl={props.resolvedUrl}
        isAdobeLaunchLoaded={props.isAdobeLaunchLoaded}
      />
    );
  }
  return <VehicleDetailsIDP {...props} />;
};

const generateHeaders = (token: string, impersonatingUser: string) => {
  return impersonatingUser
    ? {
        Authorization: `bearer ${token}`,
        ImpersonatingUser: impersonatingUser,
      }
    : {
        Authorization: `bearer ${token}`,
      };
};

export const fetchVehicleDetails = async (
  token: string,
  vrm: string,
  params: any = "",
  impersonatingUser: string = ""
) => {
  const headers = generateHeaders(token, impersonatingUser);
  if (params.vdr === "vp") {
    params.requestOrigin = "VendorPortal";
    if (params.source === "Sold") {
    } else if (
      params.source === "Provisionals" &&
      params.bq !== undefined &&
      !params.bq.includes("IsProvisional")
    )
      params.bq += "|IsProvisional:true";
    else if (params.bq === undefined && params.source === "Provisionals")
      params.bq = "IsProvisional:true";
    else if (params.bq != undefined && !params.bq.includes("IsProvisional")) {
      params.bq += "|IsProvisional:false";
    } else if (params.bq === undefined) params.bq = "IsProvisional:false";
    else if (params.bq === undefined) {
      params.bq = "IsProvisional:false";
    }
  }
  const res = await fetch(
    `${process.env.buyerAPIUrl}/api/buyer/inventorydetail/${vrm}?` +
      new URLSearchParams(params),
    {
      method: "GET",
      headers,
    }
  );

  if (res.status !== 200) {
    return null;
  }

  let vehicleDetailsResponse = await res.json();

  return vehicleDetailsResponse;
};

export async function getServerSideProps(context) {
  const { vrm } = context.params;

  // Prevent the dynamic [vrm] route from handling the Auth0 callback.
  // Return auth0 props so the client-side AuthProvider handles the code exchange.
  if (vrm === "callback") {
    return {
      props: {
        isToUseAuthZero: true,
        auth0Audience: process.env.AUTH0_AUDIENCE,
        auth0ClientId: process.env.AUTH0_CLIENT_ID,
        auth0Domain: process.env.AUTH0_DOMAIN,
        auth0Scope: process.env.AUTH0_SCOPE,
        authLogoutUrl: process.env.AUTH0_LOGOUT_URL,
      },
    };
  }

  const featureFlagManager = new FeatureFlagManager();
  await featureFlagManager.initializeFeatureManager(
    process.env.APP_CONFIGURATION_URL
  );
  const useAuthZeroResult = await featureFlagManager.getFeatureFlagStatus(
    "authzero"
  );

  const isToUseAuthZero =
    !useAuthZeroResult.isError && useAuthZeroResult.isEnabled;

  if (isToUseAuthZero) {
    let props: any = {};
    props.impersonatingUser = nookies.get(context).ImpersonatingUser || "";
    props.isToUseAuthZero = isToUseAuthZero;
    props.auth0Audience = process.env.AUTH0_AUDIENCE;
    props.auth0ClientId = process.env.AUTH0_CLIENT_ID;
    props.auth0Domain = process.env.AUTH0_DOMAIN;
    props.auth0Scope = process.env.AUTH0_SCOPE;
    props.authLogoutUrl = process.env.AUTH0_LOGOUT_URL;

    const {
      params: { vrm },
      query: { FromSearch },
      resolvedUrl,
    } = context;
    props.vrm = vrm;
    props.FromSearch = FromSearch || null;
    props.resolvedUrl = resolvedUrl || "";
    props.appConfigurationUrl = process.env.APP_CONFIGURATION_URL;
    return {
      props,
    };
  } else {
    // in prod redirecting to sso
    redirectToIdp(
      context,
      getEnvironment(),
      process.env.VIRTUAL_DOMAIN,
      process.env.URL_SUFFIX
    );

    const ADMINISTRATOR = "Administrator";
    const FEATURE_VEHICLE = "Feature Vehicle";

    let props: any = {};

    const {
      params: { vrm },
      query: { VehId, FromSearch },
      resolvedUrl,
    } = context;

    const token = getToken(nookies.get(context));
    const idToken = nookies.get(context).idtoken || "";
    const impersonatingUser = nookies.get(context).ImpersonatingUser || "";

    if (!token) {
      return { props: {} };
    }

    const decodedToken: any = jwt_decode(token);

    const analyticsProps = {
      userType: decodedToken.user_type,
      aaId: decodedToken.peep_analyticsid,
      ssoId: decodedToken.sub,
      cas: decodedToken.sourceaccount_id || null,
      vehicleId: VehId || "",
    };

    let isVendorAdmin = hasUserRole(decodedToken.role, UserRoles.VENDOR_ADMIN);

    const urlParams = context.query;
    delete urlParams.vrm;
    const vehicleDetails = await fetchVehicleDetails(
      token,
      vrm,
      urlParams,
      impersonatingUser
    );

    if (vehicleDetails) {
      props = {
        ...getVehicleDetails(vehicleDetails),
        analyticsProps,
        userType: decodedToken.user_type,
        FromSearch: FromSearch || null,
        resolvedUrl: resolvedUrl || "",
        ...props,
      };
    } else {
      props = { fetchError: true, ...props };
    }

    if (token) {
      props.username = decodedToken.user_name.split(" ")[0];
      props.lastName = decodedToken.user_name.split(" ")[1];
      props.isAdmin = decodedToken.role.includes(ADMINISTRATOR);
      props.isFeatureVehicle = decodedToken.role.includes(FEATURE_VEHICLE);
      props.userRole = decodedToken.role;
      props.isVendorAdmin = isVendorAdmin;
      props.isVendor = decodedToken.is_vendor;
      props.customerNumber = decodedToken?.customer_number;

      props.appConfigurationUrl = process.env.APP_CONFIGURATION_URL;
    }

    props.idToken = idToken;

    return {
      props,
    };
  }
}

export default VehicleDetails;
